'use client';

import { Box, Link, List, ListItem, Typography } from '@mui/material';
import { usePageAction } from '@repo/hooks/usePageAction';
import { Vertical } from '@repo/supergraph/marketplace/constants';
import type { RoundupAdditionalRecommendations } from '@repo/supergraph/roundup/base/types';
import React, { useCallback } from 'react';

interface Props {
  additionalRecommendations: RoundupAdditionalRecommendations;
  vertical: Vertical;
}

export function AdditionalRecommendations({
  additionalRecommendations,
  vertical,
}: Props): React.JSX.Element | null {
  const firePageAction = usePageAction({
    actionName: 'nw_review_redirected',
    actionSection: vertical,
    actionType: 'select',
  });

  const recommendationLinkClick = useCallback(() => {
    firePageAction();
  }, [firePageAction]);

  if (!additionalRecommendations) return null;

  return (
    <Box
      data-testid="additional-recommendations"
      sx={{
        maxWidth: { xs: '100%', md: '82%' },
        margin: 'auto',
        mb: { xs: '1.75rem', md: '4rem' },
      }}
      component="section"
      aria-labelledby="additional-recommendations-title"
    >
      <Typography component="h2" id="additional-recommendations-title">
        Want to compare more options? Here are our other top picks:
      </Typography>
      <List sx={{ listStyleType: 'disc', listStylePosition: 'inside', pl: 2, py: 0 }}>
        {additionalRecommendations.map((review, index) => {
          const entity = review.marketplaceEntity;
          const { id, displayName, reviewLink, cta } = entity;
          const ctaLink = reviewLink || cta?.link;
          if (!ctaLink) return null;

          return (
            <ListItem key={`${id}-${index}`} sx={{ display: 'list-item', py: '4px', px: 0 }}>
              <Link href={ctaLink} onClick={recommendationLinkClick} target="_blank">
                {displayName}
              </Link>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
